import { oneLine } from '@angular-devkit/core/src/utils/literals';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Storage } from '@ionic/storage';
import jwt_decode from 'jwt-decode';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
//import { AuthService } from '../auth/auth.service';
import { PrimaveraAuthService } from '../auth/primavera-auth.service';
import {
  IRepRosterVendorGroup,
  IRepRosterSalesRep,
} from '../models/repRoster.model';
import { VendorGroup } from '../models/vendorGroup.model';
import { VendorSalesRep } from '../models/vendorSalesRep.model';
import { RepRosterService, CPMSService } from '../services';
import { CPMSUserInfo } from '../models/cpmsSalesRep.model';

interface TokenTeamData {
  aud: string;
  email: string;
  givenName: string;
  lastName: string;
  iss: string;
  sub: string;
  scope: string[];
}

interface TokenPartnerData {
  aud: string;
  email: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  given_name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  family_name: string;
  iss: string;
  sub: string;
  salesRepPin: string;
}

export class UserTokenInfo {
  public readonly name;
  public readonly lastName;
  public readonly email;
  public readonly id;
  public readonly cpms: string = '';

  constructor(data: TokenTeamData | TokenPartnerData) {
    if ('givenName' in data) {
      this.name = data?.givenName;
    } else {
      this.name = data?.given_name;
    }

    if ('lastName' in data) {
      this.lastName = data?.lastName;
    } else {
      this.lastName = data?.family_name;
    }

    if ('salesRepPin' in data) {
      this.cpms = data.salesRepPin;
    }

    this.email = data.email;
    this.id = data.sub;
  }

  public get fullName() {
    return `${this.name} ${this.lastName}`;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PrimaveraD2CUser {
  readonly ssoType = 'sso';
  private userInfo: UserTokenInfo;
  private vendorInfo: VendorSalesRep;
  private vendorGroupsOfVAN: VendorGroup[];

  constructor(
    private authService: PrimaveraAuthService,
    private salesRepService: RepRosterService,
    private cpmsService: CPMSService,
    private storage: Storage,
    private router: Router
  ) {}

  get info(): UserTokenInfo {
    if (!this.userInfo && this.data !== null) {
      this.userInfo = new UserTokenInfo(this.data);
    }
    return this.userInfo;
  }

  get vendor(): VendorSalesRep {
    if (!this.vendorInfo) {
      const vendorSession = JSON.parse(sessionStorage.getItem('vendorInfo'));
      if (vendorSession) {
        this.vendorInfo = vendorSession.map((data: IRepRosterSalesRep) =>
          VendorSalesRep.createFromDTO(data)
        )[0];
      }

      if (this.vendorInfo) {
        // @ts-ignore
        this.vendorInfo.vendorGroupsById = this.vendorGroupsById;
      }
    }
    return this.vendorInfo;
  }

  /**
   * Unbelievable there is no VAN into the BE. So we need to query by list of vendor groups instead
   */
  get vendorGroups(): VendorGroup[] {
    const vendorGroupSession = JSON.parse(
      sessionStorage.getItem('vendorGroupsOfVAN')
    );
    if (vendorGroupSession) {
      this.vendorGroupsOfVAN = vendorGroupSession.map(
        (data: IRepRosterVendorGroup) => VendorGroup.createFromDTO(data)
      );
    }

    return this.vendorGroupsOfVAN ? this.vendorGroupsOfVAN : [];
  }

  get vendorGroupsById(): string[] {
    const groups = this.vendorGroups;
    return groups ? groups.map((group) => group.id as string) : [];
  }

  get CPMSUserInfo(): CPMSUserInfo {
    const cpmsSession = sessionStorage.getItem('cpmsInfo');
    if (cpmsSession) {
      return JSON.parse(cpmsSession) as CPMSUserInfo;
    }
    return null;
  }

  get cpmsOrganizationId(): number {
    return this.CPMSUserInfo?.channelOrg?.chnlOrgId || null;
  }

  get cpmsOutletIds(): number[] {
    return (
      this.CPMSUserInfo?.salesRepOutletAssociations?.map(
        (assoc) => assoc.outletId
      ) || []
    );
  }

  private get data(): any {
    const token = this.authService.accessToken;
    if (!token) {
      return null;
    }
    try {
      return jwt_decode(token);
    } catch (e) {
      return null;
    }
  }

  login(typeSSO: 'vendor' | 'team', defaultUrl) {
    sessionStorage.setItem(this.ssoType, typeSSO);
    this.authService.login(defaultUrl);
  }

  logout() {
    // Clear ionic storage
    this.storage.clear();

    // Let auth service handle the complete logout flow (storage, cookies, redirect)
    this.authService.logout();
  }

  // [DPD-2758] Fetch CPMS user info via CPMSService - integrated with Primavera auth
  getCPMSUserInformation(): Observable<CPMSUserInfo> {
    if (!this.info?.id) {
      return of(null);
    }
    return this.cpmsService.getCPMSUserInfo(this.info.id);
  }

  getVendorUserInformation(): Observable<VendorSalesRep[]> {
    if (!this.info.cpms) {
      return of(null);
    }

    return this.salesRepService
      .getAll({
        q: VendorSalesRep.getVendorSalesRepQuery(
          this.info.cpms,
          VendorSalesRep.whereClauses.cpmsId
        ),
      })
      .pipe(
        take(1),
        map((result) => {
          if (result.items.length === 0) {
            return null;
          } else {
            return result.items;
          }
        })
      );
  }

  getVendorUserGroupsByVAN(): Promise<any> {
    if (!this.vendor?.vendorAccountName) {
      return new Promise((resolve) => resolve(null));
    }
    const query = oneLine`SELECT
        Id,
        Name,
        Vendor__c,
        Rep_Count__c,
        CreatedDate,
        LastModifiedDate,
        Owner.Name,
        CreatedBy.Name,
        LastModifiedBy.Name
      FROM Vendor_Group__c
      WHERE Vendor__c = '${this.vendor.vendorAccountName}'`;

    return this.salesRepService
      .getAll({
        q: query,
      })
      .pipe(
        take(1),
        map((result) => {
          if (result.items.length === 0) {
            return null;
          } else {
            return result.items;
          }
        })
      )
      .toPromise();
  }

  isLoggedIn(): boolean {
    return this.authService.hasValidToken();
  }

  isTelusMember(): boolean {
    /*const userInfo = this.info;
    if (userInfo && userInfo.id) {
      const firstCharacterUserName = userInfo?.id[0] || 't';
      return (
        firstCharacterUserName.toLowerCase() === 't' ||
        firstCharacterUserName.toLowerCase() === 'x'
      );
    }
    return false;*/
    const ssoType = sessionStorage.getItem('sso');
    return ssoType === 'team';
  }

  public processLogout() {
    // Let auth service handle the complete logout flow (storage, cookies, redirect)
    this.authService.logout();
  }
}
